import React, { useState } from 'react';
import { ChevronDown, ChevronRight, HelpCircle, Shield } from 'lucide-react';
import JsonViewer from './JsonViewer';
import { useApi } from '../hooks/useApi';
import { useRBAC } from '../hooks/useRBAC';
import { useOnlyVerticals } from '../contexts/VerticalsContext';
import { isFairAligned as checkFairVersion } from '../utils/financialEngineUtil';

/**
 * FinancialImpactSection — collapsible financial impact panel for a risk.
 *
 * v2.0 records: Shows engine-calculated monetary impact, baseline range,
 *   applied multipliers, metadata, and a collapsible JSON audit trail.
 *
 * v3.0 records: All of the above, plus a Portfolio Contribution panel showing
 *   SLE (severity-adjusted), contribution to risk type, and implied EAL.
 *
 * v3.1 records: All of the above, plus scenario bands (Low / Expected / High)
 *   in the collapsed header and a dedicated Scenario Bands section when expanded.
 *   Low and High are display-only; Expected is the official aggregation value.
 *
 * Renders nothing if no financial data is present on the risk.
 *
 * @param {{ risk: object }} props
 *   risk fields consumed:
 *     finalCalculatedMonetaryImpact, financialBaselineMin, financialBaselineMax,
 *     sizeMultiplierApplied, revenueMultiplierApplied, regulatoryMultiplierApplied,
 *     financialCalculationJson, financialEngineVersion, aiRiskType,
 *     impactLow, impactHigh                              (v3.1 only)
 *     contributionToTypePercent, impliedExpectedAnnualContribution  (v3.0 only)
 */
const FinancialImpactSection = ({ risk }) => {
  const [isOpen] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [fairMethodOpen, setFairMethodOpen] = useState(false);
  const [scenariosOpen, setScenariosOpen] = useState(false);
  const [calcInputsOpen, setCalcInputsOpen] = useState(false);
  const [fairSnapshot, setFairSnapshot] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const apiCall = useApi();
  const { isSuperAdmin } = useRBAC();
  const verticals = useOnlyVerticals();

  // Lazy-load the FAIR snapshot once; shared by the audit-trail and calculation-inputs panels
  // so opening either (or both) triggers at most a single network call.
  const ensureSnapshotLoaded = async () => {
    if (fairSnapshot || snapshotLoading || risk.id == null) return;
    setSnapshotLoading(true);
    try {
      const data = await apiCall('GET', `/fair/risk-snapshot/${risk.id}`);
      setFairSnapshot(data);
    } catch {
      // silently fail — formulas still display without live factor data
    } finally {
      setSnapshotLoading(false);
    }
  };

  const handleAuditTrailToggle = () => {
    const willOpen = !fairMethodOpen;
    setFairMethodOpen(willOpen);
    if (willOpen) ensureSnapshotLoaded();
  };

  const handleCalcInputsToggle = () => {
    const willOpen = !calcInputsOpen;
    setCalcInputsOpen(willOpen);
    if (willOpen) ensureSnapshotLoaded();
  };

  // Only render if at least one financial calculation field is present
  const hasFinancialData =
    risk.finalCalculatedMonetaryImpact != null ||
    risk.financialBaselineMin != null ||
    risk.financialBaselineMax != null;

  if (!hasFinancialData) return null;

  // FAIR enrichment detection — central check, version-agnostic
  const isFairAligned = checkFairVersion(risk.financialEngineVersion);

  // v3.0 detection: both contribution fields must be non-null
  const isV3 =
    (risk.financialEngineVersion === 'v3.0' || risk.financialEngineVersion === 'v3.0-legacy') &&
    risk.contributionToTypePercent != null &&
    risk.impliedExpectedAnnualContribution != null;

  // v3.1 / FAIR detection: scenario band fields present
  const hasScenarioBands = risk.impactLow != null && risk.impactHigh != null;

  // Always M with 3 decimal places (e.g. $0.046M, $1.846M)
  const formatCurrency = (val) =>
    val != null ? `$${(Number(val) / 1_000_000).toFixed(3)}M` : '—';

  const formatCompact = formatCurrency;

  const formatMultiplier = (val) =>
    val != null ? `${Number(val).toFixed(2)}×` : '—';

  const formatPercent = (val) =>
    val != null ? `${(Number(val) * 100).toFixed(2)}%` : '—';

  // Signed resistance delta (e.g. -0.05 → "−0.05", 0.10 → "+0.10"). Negative = weaker control.
  const formatDelta = (val) =>
    val != null ? `${Number(val) >= 0 ? '+' : '−'}${Math.abs(Number(val)).toFixed(2)}` : '—';

  // Current org context (size/revenue aren't on the immutable snapshot — read live, labeled as such).
  const orgInfo = (() => {
    try { return JSON.parse(localStorage.getItem('selectedOrganization') || '{}'); }
    catch { return {}; }
  })();

  const verticalName = (vid) =>
    verticals?.find(v => v.verticalId === vid)?.verticalName ?? (vid != null ? `Vertical #${vid}` : '—');

  const tooltips = {
    scenarioBands:
      'Range reflects modeled uncertainty across operational and regulatory scenarios. ' +
      'Low = BaselineMin scenario, Expected (Most Likely) = official aggregation value, High = BaselineMax scenario.',
    sleLow:
      'FAIR-Aligned EAL — Low: Expected Annual Loss at conservative threat parameters. ' +
      'The frequency-weighted annual loss (LEF × loss magnitude) at the lower bound of the FAIR-Aligned ' +
      'loss curve. Represents a best-case annualized loss for this risk.',
    sleHigh:
      'FAIR-Aligned EAL — High: Expected Annual Loss at elevated threat parameters. ' +
      'The frequency-weighted annual loss (LEF × loss magnitude) at the upper bound of the FAIR-Aligned ' +
      'loss curve. Represents a worst-case annualized loss for this risk.',
    finalCalculatedMonetaryImpact:
      isFairAligned
        ? 'FAIR-Aligned Expected Annual Loss (EAL) — Most Likely: LEF × (PLM + SLM), the frequency-weighted annual loss at median threat parameters. This is the central annualized estimate within the EAL range.'
        : risk.financialEngineVersion?.startsWith('v3')
          ? 'v3.0 Single Loss Exposure (SLE): midpoint × size × revenue × regulatory multipliers × severity factor. This is the maximum expected loss from a single occurrence of this finding.'
          : 'Engine-calculated monetary impact: midpoint of the baseline range × size × revenue × regulatory multipliers.',
    financialBaselineMin:
      'Lower bound of the industry baseline financial impact range for this risk type and vertical.',
    financialBaselineMax:
      'Upper bound of the industry baseline financial impact range for this risk type and vertical.',
    sizeMultiplierApplied:
      "Multiplier based on the organization's user base / size (e.g., Enterprise = 1.50×, Small = 0.70×).",
    revenueMultiplierApplied:
      'Multiplier based on annual revenue. Defaults to 1.00 until revenue data is available.',
    regulatoryMultiplierApplied:
      'Regulatory exposure multiplier based on industry vertical (e.g., Healthcare = 1.80×, Finance = 1.70×).',
    financialEngineVersion:
      'Version of the financial calculation engine that produced this result.',
    aiRiskType:
      'Short internal code classifying the risk category (e.g., DB = Data Breach, RW = Ransomware, RA = Remote Access).',
    // v3.0 tooltips
    contributionToTypePercent:
      "This finding's proportional weight within its deduplicated risk type group. " +
      "Computed as this finding's SLE divided by the sum of all SLEs in the same risk type. " +
      "All findings in a risk type sum to 100%.",
    impliedExpectedAnnualContribution:
      "This finding's share of the risk type's probability-weighted expected annual loss. " +
      "Computed as the risk type's EAL × contribution weight. " +
      "Sum of all findings in a risk type equals the risk type's total EAL exactly.",
    // v3.1 EAL band tooltips
    impliedExpectedAnnualContributionLow:
      "Low scenario EAL contribution — uses the same deduplication, log dampening, and " +
      "probability pipeline as Expected, but with the BaselineMin SLE path. " +
      "Sum of all findings in a risk type equals the risk type's Low EAL exactly.",
    impliedExpectedAnnualContributionHigh:
      "High scenario EAL contribution — same pipeline as Expected, using BaselineMax SLE path. " +
      "Sum of all findings in a risk type equals the risk type's High EAL exactly.",
  };

  const handleTooltipToggle = (field) => {
    setActiveTooltip(activeTooltip === field ? null : field);
  };

  const TooltipIcon = ({ field }) => (
    <button
      onClick={() => handleTooltipToggle(field)}
      className="ml-1 text-gray-400 hover:text-indigo-600 transition-colors focus:outline-none"
      aria-label={`Info about ${field}`}
    >
      <HelpCircle size={13} />
    </button>
  );

  const Row = ({ label, value, field }) => (
    <div>
      <div className="flex justify-between items-center py-1.5 border-b border-gray-100 last:border-0">
        <span className="text-xs text-gray-500 flex items-center">
          {label}
          {field && <TooltipIcon field={field} />}
        </span>
        <span className="text-xs font-semibold text-gray-800">{value}</span>
      </div>
      {activeTooltip === field && tooltips[field] && (
        <div className="text-xs text-indigo-700 bg-indigo-50 rounded px-2 py-1.5 mb-1 border border-indigo-100 leading-relaxed">
          {tooltips[field]}
        </div>
      )}
    </div>
  );

  return (
    <div className="mb-4 rounded-lg border border-emerald-200 overflow-hidden">
      {/* Header — non-expandable for production */}
      <div
        className="w-full flex items-center justify-between px-4 py-3 bg-emerald-50 text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-emerald-900">
            💹 Financial Impact
          </span>
          {false && risk.financialEngineVersion && (
            <span className="px-1.5 py-0.5 bg-emerald-200 text-emerald-800 text-xs rounded font-mono">
              {risk.financialEngineVersion}
            </span>
          )}
        </div>
        {risk.finalCalculatedMonetaryImpact != null && (
          hasScenarioBands ? (
            <span className="ml-2 flex-shrink-0 text-right">
              <span className="text-base font-bold text-emerald-700">
                {formatCompact(risk.impactLow)} – {formatCompact(risk.impactHigh)}+
              </span>
              <span className="block text-xs text-emerald-600">
                Most Likely: {formatCompact(risk.finalCalculatedMonetaryImpact)}
              </span>
            </span>
          ) : (
            <span className="text-base font-bold text-emerald-700 ml-2 flex-shrink-0">
              {formatCurrency(risk.finalCalculatedMonetaryImpact)}
            </span>
          )
        )}
      </div>

      {/* Impact amounts — always visible */}
      <div className="px-4 py-4 bg-white">
        {/* Hero figure — Impact Range grid for v3.x; single value for older records */}
        {!isFairAligned && (hasScenarioBands ? (
          <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100">
            <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
              Impact Range
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-lg font-bold text-blue-600">{formatCurrency(risk.impactLow)}</div>
                <div className="text-xs text-gray-400 mt-0.5">Low</div>
              </div>
              <div className="border-x border-emerald-200">
                <div className="text-lg font-bold text-emerald-700">{formatCurrency(risk.finalCalculatedMonetaryImpact)}</div>
                <div className="text-xs text-gray-500 mt-0.5">Most Likely (Expected)</div>
              </div>
              <div>
                <div className="text-lg font-bold text-orange-600">{formatCurrency(risk.impactHigh)}+</div>
                <div className="text-xs text-gray-400 mt-0.5">High</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-3 bg-emerald-50 rounded-lg text-center border border-emerald-100">
            <div className="text-2xl font-bold text-emerald-700">
              {formatCurrency(risk.finalCalculatedMonetaryImpact)}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {isV3 ? 'Single Loss Exposure (SLE)' : 'Final Calculated Monetary Impact'}
            </div>
          </div>
        ))}

        {/* Baseline range / EAL range */}
        {isFairAligned ? (
          <div className="mb-3 mt-3">
            <div className="text-xs font-bold text-gray-500 mb-1 uppercase tracking-wide">
              FAIR-Aligned EAL Range
            </div>
            <div className="bg-gray-50 rounded p-2">
              <div className="flex justify-between items-center py-1.5">
                <span className="text-xs text-gray-500 flex items-center">
                  EAL Range (Low – High)
                  <TooltipIcon field="sleLow" />
                  <TooltipIcon field="sleHigh" />
                </span>
                <span className="text-xs font-semibold text-gray-800">
                  {formatCompact(risk.impactLow)} – {formatCompact(risk.impactHigh)}+
                </span>
              </div>
              {(activeTooltip === 'sleLow' || activeTooltip === 'sleHigh') && tooltips[activeTooltip] && (
                <div className="text-xs text-indigo-700 bg-indigo-50 rounded px-2 py-1.5 mb-1 border border-indigo-100 leading-relaxed">
                  {tooltips[activeTooltip]}
                </div>
              )}
              <div className="flex justify-between items-center py-1 border-t border-gray-100">
                <span className="text-xs text-gray-400 flex items-center">
                  Most Likely
                  <TooltipIcon field="finalCalculatedMonetaryImpact" />
                </span>
                <span className="text-xs font-medium text-gray-600">
                  {formatCurrency(risk.finalCalculatedMonetaryImpact)}
                </span>
              </div>
              {activeTooltip === 'finalCalculatedMonetaryImpact' && tooltips['finalCalculatedMonetaryImpact'] && (
                <div className="text-xs text-indigo-700 bg-indigo-50 rounded px-2 py-1.5 mb-1 border border-indigo-100 leading-relaxed">
                  {tooltips['finalCalculatedMonetaryImpact']}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="mb-3 mt-3">
            <div className="text-xs font-bold text-gray-500 mb-1 uppercase tracking-wide">
              Industry Baseline Range
            </div>
            <div className="bg-gray-50 rounded p-2">
              <Row label="Baseline Min" value={formatCurrency(risk.financialBaselineMin)} field="financialBaselineMin" />
              <Row label="Baseline Max" value={formatCurrency(risk.financialBaselineMax)} field="financialBaselineMax" />
            </div>
          </div>
        )}

        {/* Applied multipliers — v3 engine only, not applicable to FAIR */}
        {!isFairAligned && <div className="mb-3">
          <div className="text-xs font-bold text-gray-500 mb-1 uppercase tracking-wide">
            Applied Multipliers
          </div>
          <div className="bg-gray-50 rounded p-2">
            <Row label="Size Multiplier" value={formatMultiplier(risk.sizeMultiplierApplied)} field="sizeMultiplierApplied" />
            <Row label="Revenue Multiplier" value={formatMultiplier(risk.revenueMultiplierApplied)} field="revenueMultiplierApplied" />
            <Row label="Regulatory Multiplier" value={formatMultiplier(risk.regulatoryMultiplierApplied)} field="regulatoryMultiplierApplied" />
          </div>
        </div>}

        {/* Portfolio Contribution v3.0 — hidden for production */}
        {false && isV3 && (
          <div className="mb-3">
            <div className="text-xs font-bold text-gray-500 mb-1 uppercase tracking-wide flex items-center gap-1">
              Portfolio Contribution
              <span className="px-1 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded font-mono normal-case">v3.0</span>
            </div>
            <div className="bg-indigo-50 rounded p-2 border border-indigo-100">
              <Row label="Single Loss Exposure (SLE)" value={formatCurrency(risk.finalCalculatedMonetaryImpact)} field="finalCalculatedMonetaryImpact" />
              <Row label="Contribution to Risk Type" value={formatPercent(risk.contributionToTypePercent)} field="contributionToTypePercent" />
              <Row label="Implied Expected Annual Loss" value={formatCurrency(risk.impliedExpectedAnnualContribution)} field="impliedExpectedAnnualContribution" />
              {risk.impliedExpectedAnnualContributionLow != null && (
                <Row label="EAL — Low Scenario" value={formatCurrency(risk.impliedExpectedAnnualContributionLow)} field="impliedExpectedAnnualContributionLow" />
              )}
              {risk.impliedExpectedAnnualContributionHigh != null && (
                <Row label="EAL — High Scenario" value={formatCurrency(risk.impliedExpectedAnnualContributionHigh)} field="impliedExpectedAnnualContributionHigh" />
              )}
            </div>
          </div>
        )}

        {/* Metadata — hidden for production */}
        {false && <div className="mb-3">
          <div className="text-xs font-bold text-gray-500 mb-1 uppercase tracking-wide">Metadata</div>
          <div className="bg-gray-50 rounded p-2">
            <Row label="Risk Type Code" value={risk.aiRiskType || '—'} field="aiRiskType" />
            <Row label="Engine Version" value={risk.financialEngineVersion || '—'} field="financialEngineVersion" />
          </div>
        </div>}

        {/* Impact Scenarios card — FAIR only */}
        {isFairAligned && (
          <div className="mt-3 rounded-lg border border-amber-200 overflow-hidden">
            <button
              onClick={() => setScenariosOpen(o => !o)}
              className="w-full flex items-center justify-between px-3 py-2 bg-amber-50 hover:bg-amber-100 transition-colors text-left"
            >
              <span className="text-xs font-semibold text-amber-900">View Impact Scenarios</span>
              {scenariosOpen
                ? <ChevronDown size={14} className="text-amber-600 flex-shrink-0" />
                : <ChevronRight size={14} className="text-amber-600 flex-shrink-0" />}
            </button>
            {scenariosOpen && (
              <div className="bg-white p-3 text-xs">
                {(() => {
                  const low  = Number(risk.impactLow  ?? 0);
                  const most = Number(risk.finalCalculatedMonetaryImpact ?? 0);
                  const high = Number(risk.impactHigh ?? 0);
                  const cats = [
                    ['Direct',     0.30],
                    ['Bus. Int.',  0.25],
                    ['Recovery',   0.20],
                    ['Regulatory', 0.15],
                    ['Reputation', 0.10],
                  ];
                  const fmtRange = (lo, hi) => `${formatCompact(lo)} – ${formatCompact(hi)}+`;
                  return (
                    <div className="grid grid-cols-3 gap-2">
                      {/* Conservative */}
                      <div className="rounded-lg border border-blue-100 bg-blue-50 p-2.5">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <div className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />
                          <span className="font-semibold text-blue-700 text-xs">Conservative</span>
                        </div>
                        <div className="text-sm font-bold text-blue-800 mb-1">
                          {fmtRange(low * 0.8, low)}
                        </div>
                        <div className="space-y-0.5 border-t border-blue-100 pt-1.5">
                          {cats.map(([label, pct]) => (
                            <div key={label} className="flex justify-between gap-1">
                              <span className="text-gray-400 shrink-0">{label}</span>
                              <span className="font-medium text-blue-600 text-right">{fmtRange(low * 0.8 * pct, low * pct)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      {/* Base */}
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2.5">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <div className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                          <span className="font-semibold text-emerald-700 text-xs">Base (Most Likely)</span>
                        </div>
                        <div className="text-sm font-bold text-emerald-800 mb-1">
                          {fmtRange(low, most)}
                        </div>
                        <div className="space-y-0.5 border-t border-emerald-100 pt-1.5">
                          {cats.map(([label, pct]) => (
                            <div key={label} className="flex justify-between gap-1">
                              <span className="text-gray-400 shrink-0">{label}</span>
                              <span className="font-medium text-emerald-700 text-right">{fmtRange(low * pct, most * pct)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      {/* Severity */}
                      <div className="rounded-lg border border-orange-200 bg-orange-50 p-2.5">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <div className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" />
                          <span className="font-semibold text-orange-700 text-xs">Severity</span>
                        </div>
                        <div className="text-sm font-bold text-orange-800 mb-1">
                          {fmtRange(most, high)}
                        </div>
                        <div className="space-y-0.5 border-t border-orange-100 pt-1.5">
                          {cats.map(([label, pct]) => (
                            <div key={label} className="flex justify-between gap-1">
                              <span className="text-gray-400 shrink-0">{label}</span>
                              <span className="font-medium text-orange-600 text-right">{fmtRange(most * pct, high * pct)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* Calculation audit trail — super-admin only, same gate as Calculation Inputs below.
            Frontend-gated only; the underlying data is already present on the risk payload. */}
        {isSuperAdmin() && (isFairAligned ? (
          <div className="mt-3 rounded-lg border border-blue-100 overflow-hidden">
            <button
              onClick={handleAuditTrailToggle}
              className="w-full flex items-center justify-between px-3 py-2 bg-blue-50 hover:bg-blue-100 transition-colors text-left"
            >
              <span className="flex items-center gap-2">
                <span className="text-xs font-semibold text-blue-900">View Calculation Audit Trail</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-purple-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                  <Shield size={9} /> Super Admin
                </span>
              </span>
              {fairMethodOpen
                ? <ChevronDown size={14} className="text-blue-600 flex-shrink-0" />
                : <ChevronRight size={14} className="text-blue-600 flex-shrink-0" />}
            </button>
            {fairMethodOpen && (
              <div className="px-3 py-3 bg-white text-xs space-y-3">
                {/* FAIR factor inputs from snapshot — lazy-loaded on open */}
                {snapshotLoading && (
                  <div className="text-gray-400 text-xs">Loading factor data…</div>
                )}
                {fairSnapshot && (
                  <div>
                    <div className="font-bold text-blue-900 mb-1.5 uppercase tracking-wide">FAIR-Aligned Factor Inputs</div>
                    <div className="bg-blue-50 rounded p-2 space-y-0.5">
                      <div className="flex justify-between py-0.5">
                        <span className="text-gray-500">Business Value (Asset)</span>
                        <span className="font-semibold text-gray-800">{formatCurrency(fairSnapshot.businessValueUsd)}</span>
                      </div>
                      <div className="flex justify-between py-0.5">
                        <span className="text-gray-500">Contact Frequency (CF)</span>
                        <span className="font-semibold text-gray-800">{Number(fairSnapshot.contactFrequencyPerYear).toFixed(2)}/yr</span>
                      </div>
                      <div className="flex justify-between py-0.5">
                        <span className="text-gray-500">Prob. of Action (PoA)</span>
                        <span className="font-semibold text-gray-800">{(Number(fairSnapshot.probabilityOfAction) * 100).toFixed(0)}%</span>
                      </div>
                      <div className="flex justify-between py-0.5 font-medium">
                        <span className="text-gray-600">TEF (CF × PoA)</span>
                        <span className="text-gray-800">{Number(fairSnapshot.tef).toFixed(3)} events/yr</span>
                      </div>
                      <div className="flex justify-between py-0.5 border-t border-blue-100 mt-0.5 pt-1">
                        <span className="text-gray-500">Threat Capability (TCap)</span>
                        <span className="font-semibold text-gray-800">{(Number(fairSnapshot.threatCapability) * 100).toFixed(0)}%</span>
                      </div>
                      <div className="flex justify-between py-0.5">
                        <span className="text-gray-500">Control Strength (CS)</span>
                        <span className="font-semibold text-gray-800">{(Number(fairSnapshot.controlStrength) * 100).toFixed(0)}%</span>
                      </div>
                      <div className="flex justify-between py-0.5 font-medium">
                        <span className="text-gray-600">Vulnerability (VULN)</span>
                        <span className="text-gray-800">{(Number(fairSnapshot.vuln) * 100).toFixed(0)}%</span>
                      </div>
                      <div className="flex justify-between py-0.5 font-medium">
                        <span className="text-gray-600">LEF (TEF × VULN)</span>
                        <span className="text-gray-800">{Number(fairSnapshot.lef).toFixed(4)} events/yr</span>
                      </div>
                      <div className="flex justify-between py-0.5 border-t border-blue-100 mt-0.5 pt-1">
                        <span className="text-gray-500">CIA Impact Factor</span>
                        <span className="font-semibold text-gray-800">{(Number(fairSnapshot.impactFactor) * 100).toFixed(1)}%</span>
                      </div>
                      <div className="flex justify-between py-0.5">
                        <span className="text-gray-500">SLM Multiplier (secondary loss)</span>
                        <span className="font-semibold text-gray-800">{(Number(fairSnapshot.secondaryLossMultiplier) * 100).toFixed(0)}%</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Formula reference */}
                <div>
                  <div className="font-bold text-blue-900 mb-2 uppercase tracking-wide">FAIR-Aligned Calculation Method</div>
                  <div className="space-y-1 font-mono text-blue-700">
                    <div>ImpactFactor = C/10×cW + I/10×iW + A/10×aW</div>
                    <div>SLE = BusinessValue × ImpactFactor</div>
                    <div>TEF = ContactFrequency × ProbabilityOfContact</div>
                    <div>VULN = sigmoid(ThreatCapability − ControlStrength)</div>
                    <div>LEF = TEF × VULN</div>
                    <div>EAL (annual) = LEF × SLE</div>
                  </div>
                  <div className="mt-2 text-blue-600">
                    FAIR-aligned methodology with CIA-weighted impact factors (C = Confidentiality, I = Integrity, A = Availability)
                    calibrated via AI-assisted analysis against NAICS vertical benchmarks.
                    Low / Most Likely / High bands reflect conservative, median, and elevated threat parameters.
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          risk.financialCalculationJson && (
            <JsonViewer
              json={risk.financialCalculationJson}
              label={(
                <span className="flex items-center gap-2">
                  View Calculation Audit Trail
                  <span className="inline-flex items-center gap-1 rounded-full bg-purple-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                    <Shield size={9} /> Super Admin
                  </span>
                </span>
              )}
            />
          )
        ))}

        {/* Calculation inputs — super-admin only. Shows every raw knob that fed the FAIR calc.
            Frontend-gated only; the underlying snapshot endpoint returns the data to any viewer. */}
        {isSuperAdmin() && isFairAligned && (
          <div className="mt-3 rounded-lg border border-purple-100 overflow-hidden">
            <button
              onClick={handleCalcInputsToggle}
              className="w-full flex items-center justify-between px-3 py-2 bg-purple-50 hover:bg-purple-100 transition-colors text-left"
            >
              <span className="flex items-center gap-2">
                <span className="text-xs font-semibold text-purple-900">View Calculation Inputs</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-purple-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                  <Shield size={9} /> Super Admin
                </span>
              </span>
              {calcInputsOpen
                ? <ChevronDown size={14} className="text-purple-600 flex-shrink-0" />
                : <ChevronRight size={14} className="text-purple-600 flex-shrink-0" />}
            </button>
            {calcInputsOpen && (
              <div className="px-3 py-3 bg-white text-xs space-y-3">
                {snapshotLoading && (
                  <div className="text-gray-400 text-xs">Loading calculation inputs…</div>
                )}
                {!snapshotLoading && !fairSnapshot?.metadata && (
                  <div className="text-gray-400 text-xs">
                    No input metadata for this risk — it predates input capture. Re-run enrichment to populate.
                  </div>
                )}
                {fairSnapshot?.metadata && (() => {
                  const m = fairSnapshot.metadata;
                  const hasRawInputs = m.severityScoreRaw !== undefined || m.controlAnswerRaw !== undefined;
                  const Row = ({ label, value, strong }) => (
                    <div className={`flex justify-between py-0.5${strong ? ' font-medium' : ''}`}>
                      <span className={strong ? 'text-gray-600' : 'text-gray-500'}>{label}</span>
                      <span className={strong ? 'text-gray-800' : 'font-semibold text-gray-800'}>{value}</span>
                    </div>
                  );
                  return (
                    <>
                      {/* ── Raw inputs ── */}
                      <div>
                        <div className="font-bold text-purple-900 mb-1.5 uppercase tracking-wide">Raw Inputs</div>
                        <div className="bg-purple-50 rounded p-2 space-y-0.5">
                          <Row
                            label="Severity Score"
                            value={m.severityScoreRaw != null ? `${m.severityScoreRaw}/100 (Δ ${formatDelta(m.severityDelta)})` : `not used (Δ ${formatDelta(m.severityDelta)})`}
                          />
                          <Row
                            label="Question / Control Answer"
                            value={m.controlDeltaSource === 'answer+severity'
                              ? `${m.controlAnswerRaw ?? 'answered'} + severity (blended Δ ${formatDelta(m.controlDelta)})`
                              : m.controlAnswerUsed
                                ? `${m.controlAnswerRaw ?? 'answered'} (Δ ${formatDelta(m.controlDelta)})`
                                : 'none — severity proxy used'}
                          />
                          <Row label="Org Size" value={orgInfo?.companySize || '—'} />
                          <Row label="Annual Revenue" value={orgInfo?.annualRevenue || '—'} />
                          <Row label="Industry Vertical" value={verticalName(m.verticalId)} />
                          <Row label="Business Value (Asset)" value={formatCurrency(fairSnapshot.businessValueUsd)} strong />
                        </div>
                        {!hasRawInputs && (
                          <div className="mt-1 text-[11px] text-gray-400">
                            Severity/answer shown as derived deltas only; org size/revenue reflect current values, not calc-time.
                          </div>
                        )}
                      </div>

                      {/* ── Classification ── */}
                      <div>
                        <div className="font-bold text-purple-900 mb-1.5 uppercase tracking-wide">Classification</div>
                        <div className="bg-purple-50 rounded p-2 space-y-0.5">
                          <Row
                            label="Risk Category"
                            value={`${m.category ?? '—'}${m.categoryConfidence != null ? ` (${(Number(m.categoryConfidence) * 100).toFixed(0)}%)` : ''}`}
                          />
                          {m.categorySource && <Row label="Category Source" value={m.categorySource} />}
                          <Row label="Asset Class" value={m.assetClass ?? '—'} />
                          <Row label="Threat Archetype" value={m.archetype ?? '—'} />
                          <Row
                            label="Likelihood Signal"
                            value={m.likelihoodSignal != null ? `${m.likelihoodSignal}/100` : '— (derived path)'}
                          />
                          <Row
                            label="Contact Frequency"
                            value={m.cfArchetype != null && m.cfMultiplier != null
                              ? `${Number(m.cfArchetype).toFixed(2)}/yr × ${Number(m.cfMultiplier).toFixed(3)} → ${Number(m.contactFrequency).toFixed(3)}/yr`
                              : m.contactFrequency != null ? `${Number(m.contactFrequency).toFixed(2)}/yr` : '—'}
                          />
                          <Row
                            label="LLM Classification"
                            value={m.llmPromptVersion ? `${m.llmPromptVersion}${m.llmFromCache ? ' (cached)' : ''}` : '—'}
                          />
                          <Row label="CIA Delta" value={m.ciaDelta ?? '—'} />
                        </div>
                      </div>

                      {/* ── Multipliers ── */}
                      <div>
                        <div className="font-bold text-purple-900 mb-1.5 uppercase tracking-wide">Multipliers</div>
                        <div className="bg-purple-50 rounded p-2 space-y-0.5">
                          <Row label="Control Strength (base → effective)" value={`${formatPercent(m.csBase)} → ${formatPercent(m.csEffective)}`} />
                          <Row label="SLE Multiplier (category, confidence-blended)" value={formatMultiplier(m.sleMult)} />
                          <Row label="Asset Multiplier" value={formatMultiplier(m.assetMult)} />
                          <Row label="Combined SLE Multiplier" value={formatMultiplier(m.combinedSleMult)} strong />
                          <Row label="Fines Multiplier" value={formatMultiplier(m.finesMultiplier)} />
                          <Row label="Secondary Loss (base → effective)" value={`${formatPercent(m.secondaryLossMultiplierBase)} → ${formatPercent(m.slmEffective)}`} />
                        </div>
                      </div>

                      {/* ── Consequence Profile ("Risk DNA") — per-risk dimensions driving each FAIR axis ── */}
                      {m.consequenceProfile && (() => {
                        const cp = m.consequenceProfile;
                        const eff = m.consequenceAxisEffects || {};
                        const groups = [
                          {
                            axis: 'Vulnerability',
                            note: eff.resistanceDelta != null ? `resistance Δ ${formatDelta(eff.resistanceDelta)}` : null,
                            dims: [
                              ['Identity Compromise', cp.identityCompromise],
                              ['Lateral Movement', cp.lateralMovement],
                            ],
                          },
                          {
                            axis: 'Primary Loss',
                            note: eff.sleMult != null ? `SLE ×${formatMultiplier(eff.sleMult)}` : null,
                            dims: [
                              ['Operational Disruption', cp.operationalDisruption],
                              ['Data Confidentiality', cp.dataConfidentiality],
                              ['Data Integrity', cp.dataIntegrity],
                              ['Availability Impact', cp.availabilityImpact],
                              ['Financial Fraud Potential', cp.financialFraud],
                              ['Customer Impact', cp.customerImpact],
                              ['Recovery Complexity', cp.recoveryComplexity],
                              ['Time to Recover', cp.timeToRecover],
                            ],
                          },
                          {
                            axis: 'Secondary Loss',
                            note: eff.secondaryMult != null ? `SLM ×${formatMultiplier(eff.secondaryMult)}` : null,
                            dims: [
                              ['Regulatory Exposure', cp.regulatoryExposure],
                              ['Legal Liability', cp.legalLiability],
                              ['Reputation Damage', cp.reputationDamage],
                            ],
                          },
                        ];
                        return (
                          <div>
                            <div className="font-bold text-purple-900 mb-1.5 uppercase tracking-wide">
                              Consequence Profile (Risk DNA)
                            </div>
                            <div className="bg-purple-50 rounded p-2 space-y-2">
                              {groups.map((g) => (
                                <div key={g.axis}>
                                  <div className="flex justify-between text-[11px] font-semibold text-purple-700 mb-0.5">
                                    <span>{g.axis}</span>
                                    {g.note && <span className="text-gray-500 font-normal">{g.note}</span>}
                                  </div>
                                  {g.dims.map(([label, score]) => (
                                    <Row key={label} label={label} value={score != null ? `${score}/100` : '—'} />
                                  ))}
                                </div>
                              ))}
                            </div>
                            <div className="mt-1 text-[11px] text-gray-400">
                              Per-risk consequence scores (0–100) that differentiate this risk's FAIR-Aligned factors.
                              {m.consequenceProfileSource ? ` Source: ${m.consequenceProfileSource}` : ''}
                              {m.consequenceProfileSource === 'llm' && (m.severityScoreRaw != null || (m.assetClass && m.assetClass !== 'Internal'))
                                ? ' · severity/asset context applied'
                                : ''}
                              {m.consequenceProfileSource ? '.' : ''}
                            </div>
                          </div>
                        );
                      })()}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancialImpactSection;
